/**
 * Autosave, wired.
 *
 * Everything downstream of this has existed and been tested for weeks —
 * `createSaveController` for the schedule, `saveLocalProject` for the durable
 * copy, `saveVersion` for the shared one, `setSaveState` for the indicator —
 * and none of it had a caller. The editor persisted exactly one thing: the
 * colour picker's recent swatches. This is the file that connects them, and it
 * is deliberately the only place in `src/app` that knows `src/api` exists.
 *
 * Three decisions worth stating, because each has a wrong version that looks
 * reasonable:
 *
 *   1. **A project exists before an account does.** The local id is minted on
 *      first boot and the work is written to IndexedDB whether or not anybody
 *      is signed in. Requiring sign-in to save would make the common case —
 *      open the site, draw something — the case that loses work.
 *
 *   2. **The cloud copy is created lazily, and its failure is not an error.**
 *      Over quota, offline, signed out: all of them leave the local copy
 *      written and the message honest about where the work is.
 *
 *   3. **Restore happens before the default layer is added.** Otherwise every
 *      reload appends an empty "Artwork" above the restored stack, which reads
 *      as the editor quietly adding junk to your file.
 */
import { FORMAT_VERSION_OUTFIT, type GarmentDoc, type GarmentType } from "../../shared/format";
import { unpackProject } from "../../shared/pack";
import { ApiError } from "../api/client";
import { createProject, getProject, loadBlob, saveVersion } from "../api/projects";
import { identity, onIdentityChange, refresh } from "../auth/session";
import { setSaveState } from "../hud/app-bar";
import { currentProjectId, setCurrentProjectId } from "../hud/current-project";
import { deviceLabel } from "../hud/device-label";
import {
  listLocalProjects,
  markMigrated,
  readLocalMeta,
  readLocalProject,
  saveLocalProject,
} from "../hud/local-store";
import { onProjectEvent } from "../hud/project-events";
import { createSaveController, type SaveController } from "../hud/save-controller";
import type { LayerStack } from "./layers";
import { createSnapshotter, hydrate, type ProjectSnapshot } from "./snapshot";
import type { CloudStatus } from "./versions";

/**
 * The name a design has before anybody names it.
 *
 * One per garment, because "Untitled shirt" on a pair of pants is the document
 * disagreeing with itself in the two places a person actually looks — the app
 * bar and the Designs list.
 */
const DEFAULT_TITLES: Record<"shirt" | "pants", string> = {
  shirt: "Untitled shirt",
  pants: "Untitled pants",
};
const DEFAULT_TITLE: string = DEFAULT_TITLES.shirt;

/** True while the design still has a name nobody chose. */
function isUnnamed(t: string): boolean {
  return t === DEFAULT_TITLES.shirt || t === DEFAULT_TITLES.pants;
}

/**
 * The garments this editor can actually draw.
 *
 * Narrower than `GARMENT_TYPES` in `shared/format.ts`, and deliberately so:
 * that list is every garment the *format* knows, this is every garment the
 * *editor* has a template and a region map for. Named apart so the difference
 * cannot be mistaken for duplication and "tidied up".
 *
 * `GarmentType` also includes `"tshirt"`, which is deliberately not here: a
 * t-shirt is a plain square image with no template, no regions and no edge
 * pipeline, so none of the model this editor is built on applies to it.
 * Accepting one out of an archive would load the shirt guide and silently claim
 * to be editing something it is not.
 */
const DRAWABLE_GARMENTS: readonly GarmentType[] = ["shirt", "pants"];

function mintId(): string {
  // randomUUID needs a secure context; the timestamp keeps ids unique enough on
  // plain http during local development rather than throwing at boot.
  return globalThis.crypto?.randomUUID?.() ?? `local-${Date.now()}-${performance.now()}`;
}

const rememberId = setCurrentProjectId;

/**
 * The project this browser was last editing.
 *
 * Null rather than a minted id, so the caller can fall back to the most recent
 * design in IndexedDB. That fallback matters: when localStorage is blocked but
 * IndexedDB is not — Safari in some configurations, or a user who cleared site
 * data selectively — minting here would hand out a fresh id every single boot,
 * so the editor would open blank forever while orphaned archives piled up in
 * storage, one per visit.
 */
const rememberedId = currentProjectId;

/** Who is signed in, by the same rule the designs page uses. */
const accountOf = (id: ReturnType<typeof identity>) => id.user?.id ?? id.devUser ?? null;

/** The design edited most recently on this device, if there is one. */
async function mostRecentLocalId(): Promise<string | null> {
  try {
    // Already sorted newest first by the store.
    const [newest] = await listLocalProjects();
    return newest?.id ?? null;
  } catch {
    return null;
  }
}

/**
 * Which design to open.
 *
 * `resume` is what a bare `/app` means and what every reload means. The other
 * two arrive as URL commands and are consumed on arrival — see `openIntentFrom`
 * for why they cannot be allowed to persist in the address bar.
 */
export type OpenIntent =
  | { kind: "resume" }
  | { kind: "new" }
  /** A design on this device, by local id. */
  | { kind: "project"; id: string }
  /** A design in the account with no copy here yet, by cloud project id. */
  | { kind: "cloud"; id: string };

/**
 * Read an open command out of a query string.
 *
 * Pure, and separate from the URL rewriting that follows it, because the rule
 * that matters is a decision rather than a side effect: `?new` must mint
 * exactly one design no matter how many times that URL is loaded.
 *
 * `?new` wins over `?p=`. Both together is not a thing any link this app writes
 * produces, so it is a hand-edited URL, and "make me a new one" is the reading
 * that cannot destroy anything.
 */
export function openIntentFrom(search: string): OpenIntent {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return { kind: "resume" };
  }
  if (params.has("new")) return { kind: "new" };

  /*
   * A local id wins over a cloud one.
   *
   * Both together is not a link this app writes, so it is hand-edited — and the
   * local copy is the one that opens without a network round trip and without
   * the risk of overwriting an account's copy with a document that failed to
   * download.
   */
  const local = params.get("p")?.trim();
  if (local) return { kind: "project", id: local };

  const cloud = params.get("c")?.trim();
  if (cloud) return { kind: "cloud", id: cloud };

  // `?p=` or `?c=` with nothing after it is a truncated link, not a request for
  // a design called "". Resuming is the harmless reading.
  return { kind: "resume" };
}

export interface AutosaveOptions {
  /**
   * The primary garment's stack — the one `manifest.garmentType` names, and the
   * one whose layers go in the archive's root `layers.json`.
   */
  stack: LayerStack;
  /**
   * Every garment's stack, including the primary.
   *
   * Separate from `stack` rather than replacing it because the archive keeps
   * the primary at the root: `peekManifest` requires it, and that is the gate
   * every upload passes through in the Worker.
   *
   * Optional so the editor's own tests and any caller that only has one stack
   * keep working; absent means "just the primary", which is what every design
   * written before outfits actually is.
   */
  stacks?: Partial<Record<GarmentType, LayerStack>>;
  /** Called once after a restore, so the caller can skip seeding a blank layer. */
  onRestored?: (info: { restored: number; dropped: string[] }) => void;
  /** Which design to open. Defaults to resuming whatever this browser had. */
  open?: OpenIntent;
}

export interface Autosave {
  /** Call after any change. Cheap; coalesces. */
  touch(): void;
  flush(): Promise<void>;
  title(): string;
  setTitle(next: string): void;
  /**
   * Shirt or pants.
   *
   * A property of the document, not of the export. The two templates are
   * geometrically identical, so the same 585×559 artwork is a valid shirt *and*
   * a valid pair of pants — which is exactly why the file has to record which
   * one was meant. Nothing about the pixels can tell you afterwards, and
   * uploading one as the other puts the chest on a shin.
   */
  garment(): GarmentType;
  setGarment(next: GarmentType): void;
  /**
   * What the cloud copy of this design is doing.
   *
   * A union rather than `string | null` because the reasons there is no id are
   * not interchangeable: signed out, not created yet, plan full and refused all
   * produce no project, and a panel that reports them identically tells three
   * of those four users something false.
   */
  cloudStatus(): CloudStatus;
  /**
   * Run something when `cloudStatus()` may have changed, or a version was made.
   *
   * Fires on a cloud save (a new version row exists), on the link being
   * established or dropped, on a refusal, and on the account changing. Returns
   * its own unsubscribe so a listener cannot outlive its registrar.
   */
  onCloudChange(fn: () => void): () => void;
  /** True when a previous session's work was put back. */
  restored: boolean;
  destroy(): void;
}

/**
 * Bring a design down from the account.
 *
 * The half that made `/app/projects` honest. It has promised that designs
 * "follow you to any computer you sign in to" since the day autosave landed,
 * and until now a design made on one machine simply had no way to open on
 * another — every piece existed and nothing called them in this order.
 *
 * Returns null and says why on every failure, so the caller falls through to a
 * blank editor rather than a broken one.
 */
async function fetchFromAccount(
  projectId: string,
): Promise<{ bytes: Uint8Array | null; revision: number; userId: string; title: string } | null> {
  /*
   * Wait for the session before deciding nobody is signed in.
   *
   * This runs during boot, and `identity()` is signed-out until `/api/me` comes
   * back — so opening a link to a design in your account raced that request and
   * usually lost, telling a signed-in user to sign in. Everything else that
   * reads the identity happens later, on a touch or an identity change, which
   * is why nothing had hit this before.
   *
   * Only refreshed when it looks signed-out, so the common case costs nothing.
   */
  let userId = accountOf(identity());
  if (!userId) {
    await refresh().catch(() => {});
    userId = accountOf(identity());
  }
  if (!userId) {
    setSaveState({
      kind: "error",
      message: "Sign in to open a design from your account.",
    });
    return null;
  }

  try {
    const { project } = await getProject(projectId);

    /*
     * A project with no version yet.
     *
     * Reachable: `createProject` and the first upload are two calls, so a
     * failure between them leaves an empty project — and the account already
     * contains some from an earlier bug. There is nothing to download, so this
     * opens as a blank design that is already attached to it. Asking R2 for a
     * blob that was never written would 404 and read as "your design is gone".
     */
    if (!project.current_version_id) {
      return { bytes: null, revision: project.revision, userId, title: project.title };
    }

    const { bytes } = await loadBlob(projectId);
    return { bytes, revision: project.revision, userId, title: project.title };
  } catch (err) {
    const gone = err instanceof ApiError && (err.status === 404 || err.status === 403);
    setSaveState({
      kind: "error",
      message: gone
        ? "That design is not in your account any more."
        : "Could not reach your account, so this design did not open. Your work here is unaffected.",
    });
    return null;
  }
}

export async function installAutosave(options: AutosaveOptions): Promise<Autosave> {
  const { stack, onRestored } = options;
  const intent = options.open ?? { kind: "resume" };

  /*
   * Which design this session is editing.
   *
   * All three paths end by remembering the id, which is what makes the URL
   * command disposable: the next load resumes from storage and needs no query
   * string. That is the whole reason the caller strips it.
   */
  let localId: string;
  if (intent.kind === "new" || intent.kind === "cloud") {
    // A design coming down from the account has no copy here yet, so it needs a
    // local id of its own. Never the cloud project's id: the two namespaces are
    // separate, and reusing one would make `?p=` and `?c=` collide.
    localId = mintId();
    rememberId(localId);
  } else if (intent.kind === "project") {
    localId = intent.id;
    rememberId(localId);
  } else {
    localId = rememberedId() ?? "";
    if (!localId) {
      // Nothing remembered: either a first visit, or localStorage is blocked.
      // The most recently edited local design is the right guess for the second
      // case and harmlessly absent in the first.
      localId = (await mostRecentLocalId()) ?? mintId();
      rememberId(localId);
    }
  }

  let title = DEFAULT_TITLE;
  let createdAt = Date.now();
  let garment: GarmentType = "shirt";
  let restored = false;

  // ------------------------------------------------------------- restore

  /*
   * Reading and rebuilding are separate failures, and they need opposite
   * responses. This used to be one try/catch, and the comment in it claimed the
   * bytes stayed safe on disk — which was false three seconds later.
   *
   *   - **The read failed.** Storage is unavailable or broken. Saving will fail
   *     too and the controller will say so. Keep the id: there is nothing to
   *     protect the archive from, because nothing is going to be written.
   *
   *   - **The archive would not open.** The bytes are real and this build
   *     cannot use them — corrupt, truncated, or written by a newer deploy that
   *     the user has since been served an older cached build of. Keeping the id
   *     means the first brush stroke overwrites a file we merely failed to
   *     read, destroying work that was never damaged. So the session rotates to
   *     a new id and the original stays untouched, listed on /app/projects,
   *     exportable.
   */
  let bytes: Uint8Array | null = null;
  let storageBroken = false;
  /**
   * Set when this design came down from the account.
   *
   * Held until the controller exists, then used to attach it — so the first
   * save goes to the project it was opened from rather than creating a second
   * one, and goes at the revision the server is actually on rather than zero.
   */
  let adopted: { projectId: string; revision: number; userId: string } | null = null;

  if (intent.kind === "cloud") {
    const fetched = await fetchFromAccount(intent.id);
    if (fetched) {
      bytes = fetched.bytes;
      adopted = { projectId: intent.id, revision: fetched.revision, userId: fetched.userId };
      title = fetched.title;
    }
  } else if (intent.kind !== "new") {
    // A brand-new design has nothing behind its id by construction, so the read
    // is skipped rather than performed and discarded.
    try {
      bytes = await readLocalProject(localId);
    } catch {
      storageBroken = true;
    }
  }

  /*
   * Asked for a specific design that is not here.
   *
   * Only reachable from a hand-edited or stale link: every card links to an id
   * it just read out of this device's storage, and `?new` never lands here
   * because the intent above skips the read. Saying so beats silently starting
   * a blank design under a stranger's id, which is what happens if this is left
   * to fall through.
   *
   * A design that is in the account but not yet on this device reads the same
   * way today. Fetching it is the next piece of work; until then this message
   * is the honest one, because nothing here can open it.
   */
  if (intent.kind === "project" && !bytes && !storageBroken) {
    setSaveState({
      kind: "error",
      message: "That design is not on this device. Anything you draw now starts a new one.",
    });
  }

  if (bytes) {
    try {
      const unpacked = unpackProject(bytes);
      title = unpacked.project.manifest.title || DEFAULT_TITLE;
      createdAt = unpacked.project.manifest.createdAt;
      // Archives written before the editor knew about garments all say "shirt",
      // which is what they were, so there is no migration to do — but an
      // unrecognised value from a newer build must not become the guide image.
      garment = DRAWABLE_GARMENTS.includes(unpacked.project.manifest.garmentType)
        ? unpacked.project.manifest.garmentType
        : "shirt";
      const info = await hydrate(stack, unpacked.project, unpacked.blobs);

      /*
       * The other garments, into their own stacks.
       *
       * `unpackProject` returns the primary in both `layers` and
       * `garments[primary]` — the same array, by reference — so the primary is
       * skipped here rather than hydrated twice into the same stack, which
       * would double every layer in it.
       *
       * A garment with no stack to put it in is dropped and *said*, not
       * silently discarded: it means an archive carries something this build
       * cannot show, and a design that quietly loses its pants on open is the
       * worst version of this feature.
       */
      const extraDropped: string[] = [];
      for (const [g, doc] of Object.entries(unpacked.project.garments ?? {})) {
        if (g === garment || !doc) continue;
        const target = options.stacks?.[g as GarmentType];
        if (!target) {
          extraDropped.push(`${g} (this build has nowhere to put it)`);
          continue;
        }
        const got = await hydrate(
          target,
          { ...unpacked.project, layers: doc.layers, edge: doc.edge },
          unpacked.blobs,
        );
        extraDropped.push(...got.dropped);
      }

      restored = info.restored > 0;
      onRestored?.({
        restored: info.restored,
        // Layers the archive itself lost on the way in are reported alongside
        // the ones this build could not rebuild; to the person who made them
        // they are the same event.
        dropped: [...unpacked.droppedOnMigration, ...info.dropped, ...extraDropped],
      });

      if (adopted) {
        /*
         * Keep the copy, exactly as it arrived.
         *
         * `bytes`, not a fresh snapshot of what was just hydrated. Re-packing
         * would produce a different archive from the one the server holds
         * before the user has changed anything, so the next save would be a
         * diff against a document nobody edited — and any layer this build
         * could not rebuild would be written back as missing.
         *
         * `migratedTo` in the same write rather than a `markMigrated` after it:
         * two writes leave a window where the row exists unmigrated, and a row
         * like that is listed twice — once as itself and once as its cloud
         * twin.
         */
        await saveLocalProject({
          id: localId,
          title,
          bytes,
          garment,
          claimedBy: adopted.userId,
          migratedTo: { userId: adopted.userId, projectId: adopted.projectId },
        });
      }
    } catch {
      /*
       * Whatever is on screen is not that design, so it must not be attached to
       * it. Left adopted, the first stroke would upload a blank document over
       * the copy in the account — destroying work that downloaded perfectly
       * well and merely failed to open in this build.
       */
      adopted = null;
      localId = mintId();
      title = DEFAULT_TITLE;
      createdAt = Date.now();
      restored = false;
      // Anything hydrate managed to add before it threw is not a document, it
      // is the wreckage of one. Starting clean beats starting with half of it.
      for (const l of [...stack.layers()]) stack.remove(l.id);
      rememberId(localId);
      setSaveState({
        kind: "error",
        message:
          "Could not reopen your last design, so this is a new one. The original is safe under Designs — open it there to export it.",
      });
    }
  } else if (storageBroken) {
    setSaveState({
      kind: "error",
      message: "This browser is not letting us read saved designs. Export your work to keep it.",
    });
  }

  const snapshotter = createSnapshotter(stack, () => ({
    title,
    createdAt,
    garmentType: garment,
    appVersion: "web",
  }));

  /**
   * A snapshotter per non-primary garment.
   *
   * One each rather than one shared, because the encode cache is keyed by layer
   * id and rev — `createSnapshotter` says so in as many words — and a cache that
   * two stacks wrote into would hand one garment the other's pixels the moment
   * their ids collided. They cannot collide today (`newId` is module-scoped and
   * there is a test), but a cache per stack is the cheap way to keep that from
   * being load-bearing twice over.
   */
  const others = new Map<GarmentType, ReturnType<typeof createSnapshotter>>();
  for (const [g, s] of Object.entries(options.stacks ?? {}) as [GarmentType, LayerStack][]) {
    if (!s || g === garment) continue;
    others.set(
      g,
      createSnapshotter(s, () => ({ title, createdAt, garmentType: g, appVersion: "web" })),
    );
  }

  /**
   * The primary's snapshot, plus every other garment that has something in it.
   *
   * **An empty garment is not written.** A design holds both from the moment it
   * exists, so most of them have an empty one, and writing it would turn every
   * single-garment archive into a v3 archive — one the deployed Worker refuses,
   * for a garment with nothing in it.
   *
   * That is also why the format version is decided here rather than stamped
   * unconditionally: an archive claims v3 exactly when it carries something only
   * v3 can express, and stays v2 otherwise. See FORMAT_VERSION in shared/format.ts.
   */
  function snapshotOutfit(): Promise<ProjectSnapshot> {
    const extras = [...others.keys()].filter(
      (g) => (options.stacks?.[g]?.layers().length ?? 0) > 0,
    );
    /*
     * Nothing else to gather: hand back the primary's promise untouched.
     *
     * Not an optimisation — a timing guarantee. `createSaveController` reads
     * `cloudId` on the far side of this await to decide whether the design it
     * snapshotted still belongs where it was going, and wrapping the primary in
     * one more async frame moved a concurrent "deleted in another tab" onto the
     * other side of that read. A design with one garment therefore goes through
     * exactly the path it went through before outfits existed, await for await.
     */
    if (extras.length === 0) return snapshotter.snapshot();
    return gatherOutfit();
  }

  async function gatherOutfit(): Promise<ProjectSnapshot> {
    const base = await snapshotter.snapshot();
    const garments: Partial<Record<GarmentType, GarmentDoc>> = {
      [garment]: { layers: base.project.layers, edge: base.project.edge },
    };
    const blobs = { ...base.blobs };
    let extra = 0;

    for (const [g, snap] of others) {
      const s = options.stacks?.[g];
      if (!s || s.layers().length === 0) continue;
      const taken = await snap.snapshot();
      garments[g] = { layers: taken.project.layers, edge: taken.project.edge };
      // Flat and un-namespaced, which is safe only because layer ids are unique
      // across stacks. src/app/layer-ids.test.ts is what keeps that true.
      Object.assign(blobs, taken.blobs);
      extra += 1;
    }

    return {
      ...base,
      blobs,
      project: {
        ...base.project,
        manifest: {
          ...base.project.manifest,
          formatVersion: extra ? FORMAT_VERSION_OUTFIT : base.project.manifest.formatVersion,
          ...(extra ? { garments: Object.keys(garments) as GarmentType[] } : {}),
        },
        ...(extra ? { garments } : {}),
      },
    };
  }

  // --------------------------------------------------------------- cloud

  /**
   * Which account this design lives in, and where.
   *
   * `userId` is part of it on purpose. Without it, signing out and signing in
   * as somebody else on the same browser left the previous account's project id
   * attached: the new user's edits would be aimed at a project they do not own,
   * and — because the id was already set — nothing would ever create one for
   * them. Their work would stay local forever with the UI claiming it was fine.
   */
  let link: { userId: string; projectId: string } | null = null;

  /**
   * Why the cloud copy is not happening, when it is not.
   *
   * Retrying is only worth it for failures that can change. `createProject` was
   * previously retried from every `touch()`, so a signed-in user on a full plan
   * drawing for a minute fired a POST per edit at an endpoint that could only
   * ever answer 402 — and each timed-out attempt that actually succeeded
   * server-side spent another project.
   */
  /** Notified whenever `cloudStatus()` may have moved, or a version was made. */
  const cloudListeners = new Set<() => void>();

  /**
   * Tell the listeners, and never let one of them break a save.
   *
   * These run inside `onState` — the save indicator's own path — so a listener
   * that throws would take the indicator down with it and the user would lose
   * the one signal that says their work is safe.
   */
  function notifyCloud() {
    for (const fn of cloudListeners) {
      try {
        fn();
      } catch (err) {
        console.error("a cloud listener threw", err);
      }
    }
  }

  let blocked: "plan-full" | "denied" | null = null;
  let retryAfter = 0;
  /**
   * The account every other field in this section is a statement about.
   *
   * Tracked separately from `link`, because a refusal is cloud state too and it
   * exists precisely when `link` does not.
   */
  let cloudUser: string | null = null;
  /** Long enough that a flaky connection does not turn into a request storm. */
  const RETRY_COOLDOWN_MS = 60_000;

  /**
   * Adopt the cloud project this design already has, or create one.
   *
   * The adopt half is the half that was missing. `markMigrated` recorded the
   * project id and nothing ever read it back, so `cloudId` started null on every
   * boot and every reload created *another* cloud project for the same design —
   * three reloads exhausted a free plan's entire quota with duplicates of one
   * drawing, none of which ever received a version.
   */
  let claiming: Promise<void> | null = null;

  function ensureCloud(): Promise<void> {
    if (claiming) return claiming;
    const id = identity();
    // `signedIn || devUser`, the same predicate the projects page uses. Checking
    // only `signedIn` left the dev identity permanently local, which would have
    // made the entire cloud path unreachable in development — a seam that can
    // only be exercised in production is a seam nobody exercises.
    const userId = accountOf(id);
    if (!userId) return Promise.resolve();
    if (link?.userId === userId) return Promise.resolve();
    if (blocked === "plan-full" || blocked === "denied") return Promise.resolve();
    if (Date.now() < retryAfter) return Promise.resolve();

    claiming = (async () => {
      try {
        cloudUser = userId;
        const adopted = await adopt(userId);
        const project = adopted ?? (await create(userId));
        if (!project) return;

        link = { userId, projectId: project.id };
        // The panel can list versions now, and could not a moment ago.
        notifyCloud();
        controller.attachCloud(project.id, project.revision);

        /*
         * Push what is on screen now.
         *
         * Attaching mid-session only tells the controller where to upload; it
         * does not make the already-written local copy dirty again. Without
         * this the cloud project sat at revision 0 with no content until the
         * user happened to make another edit — which is exactly what the three
         * orphaned r0 projects in the account were.
         */
        await controller.flush();
      } catch {
        // Reaching here means an unexpected failure rather than a refusal;
        // `adopt` and `create` classify the ones we understand.
        retryAfter = Date.now() + RETRY_COOLDOWN_MS;
      } finally {
        claiming = null;
      }
    })();

    return claiming;
  }

  /** The project this design was already migrated to, if it still exists. */
  async function adopt(userId: string): Promise<{ id: string; revision: number } | null> {
    let migratedTo: { userId: string; projectId: string } | undefined;
    try {
      migratedTo = (await readLocalMeta(localId))?.migratedTo;
    } catch {
      return null;
    }
    if (!migratedTo || migratedTo.userId !== userId) return null;

    try {
      const { project } = await getProject(migratedTo.projectId);
      /*
       * The server's revision, not zero.
       *
       * `saveVersion` sends this as `X-Base-Revision`, and the server refuses a
       * stale one with a 409 rather than clobbering. Resuming at 0 against a
       * project at revision 7 means every upload for the rest of the session is
       * a conflict — the cloud copy silently stops updating while the indicator
       * shows a state the user has no way to act on.
       */
      return { id: project.id, revision: project.revision };
    } catch (err) {
      // Deleted server-side, or belongs to someone else now. Fall through to
      // creating a fresh one rather than aiming saves at a project that is gone.
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) return null;
      throw err;
    }
  }

  async function create(userId: string): Promise<{ id: string; revision: number } | null> {
    try {
      // With the garment. Omitted, every project in the account was recorded as
      // a shirt regardless of what it was, and there is no route that can
      // correct it afterwards.
      const project = await createProject(title, garment);
      try {
        // Recorded so `/app/projects` shows one row for this design rather than
        // a local copy beside its cloud twin — and so the next boot adopts this
        // project instead of creating another.
        await markMigrated(localId, { userId, projectId: project.id });
      } catch {
        // The project exists; only the local note failed. Keeping it in memory
        // means this session still uploads to the right place, and the worst
        // case is one extra project next boot rather than one per boot.
      }
      return { id: project.id, revision: project.revision };
    } catch (err) {
      if (err instanceof ApiError) {
        // Nothing about drawing more will make room, so stop asking.
        if (err.code === "quota_exceeded" || err.code === "storage_exceeded") {
          blocked = "plan-full";
          // A refusal is a status change: the panel has to stop saying
          // "saving…" and say why it will not happen.
          notifyCloud();
          return null;
        }
        // Signed out between the check and the call, or forbidden. A later
        // identity change clears this.
        if (err.status === 401 || err.status === 403) {
          blocked = "denied";
          notifyCloud();
          return null;
        }
      }
      retryAfter = Date.now() + RETRY_COOLDOWN_MS;
      return null;
    }
  }

  // ---------------------------------------------------------- controller

  const controller: SaveController = createSaveController(
    {
      snapshot: () => snapshotOutfit(),

      async saveLocal(id, projectTitle, bytes, thumb) {
        // `garment` from this closure rather than from the controller: it is
        // the same variable the snapshot just read, so the row and the archive
        // inside it cannot disagree.
        await saveLocalProject({ id, title: projectTitle, bytes, garment, thumb });
      },

      async uploadCloud(projectId, bytes, baseRevision) {
        return saveVersion(projectId, bytes, baseRevision, deviceLabel());
      },

      onState(state) {
        setSaveState(state);
        /*
         * A version row exists now, so anything listing them is stale.
         *
         * Gated on `link` because a signed-out save reports "saved" too and
         * creates no version. A listener that throws must not take the save
         * indicator down with it, hence the try.
         */
        if (state.kind === "saved" && link) notifyCloud();
      },
    },
    { localId },
  );

  /**
   * Who is signed in has changed.
   *
   * Sign-out and sign-in-as-someone-else are the same event here, and both must
   * detach: uploading to a project the current user does not own is at best a
   * 403 and at worst — on the shared devices this audience actually uses — one
   * person's drawing landing in another person's account. The controller is
   * pointed back at nothing until a link is re-established for whoever is now
   * signed in.
   *
   * The refusals are cleared too: "your plan is full" was true of the previous
   * account and says nothing about this one.
   */
  /*
   * Point the controller at the project this design came from.
   *
   * Before `ensureCloud` can run, so the first save goes to the project that
   * was opened rather than creating a second one for the same design — and at
   * the server's revision, because starting at zero makes every upload for the
   * rest of the session a conflict.
   */
  if (adopted) {
    link = { userId: adopted.userId, projectId: adopted.projectId };
    notifyCloud();
    cloudUser = adopted.userId;
    controller.attachCloud(adopted.projectId, adopted.revision);
  }

  /**
   * Somebody deleted this design in another tab.
   *
   * Two things must be true afterwards, and they pull in opposite directions:
   * the design stays deleted, and the work on screen is not lost. Continuing to
   * save under the old id fails the first — the next stroke writes the whole
   * design back and the delete silently undoes itself. Simply stopping fails
   * the second: the canvas is still full of work that now has nowhere to go,
   * and closing the tab takes it.
   *
   * So the session detaches and carries on under a fresh id. Nothing is written
   * at the moment of detaching — the document is not dirty, so no save is
   * scheduled — which means a user who deleted a design and is done with it
   * gets exactly what they asked for. It only becomes a design again if they
   * keep drawing, which is the one case where they clearly want it kept.
   *
   * Said out loud rather than done quietly: the name in the app bar does not
   * change, so without the message the tab would look identical while meaning
   * something entirely different.
   */
  const stopProjectEvents = onProjectEvent((event) => {
    if (event.kind !== "deleted" || event.id !== localId) return;

    localId = mintId();
    rememberId(localId);
    controller.retarget(localId);
    // The cloud project went with it; a new one is created if editing resumes.
    if (link) controller.detachCloud();
    link = null;
    cloudUser = null;
    blocked = null;
    retryAfter = 0;
    snapshotter.reset();
    notifyCloud();

    setSaveState({
      kind: "error",
      message: "This design was deleted in another tab. Anything you draw now saves as a new one.",
    });
  });

  const stopIdentity = onIdentityChange(() => {
    const userId = accountOf(identity());
    if (userId !== cloudUser) {
      cloudUser = userId;
      if (link) controller.detachCloud();
      link = null;
      /*
       * The refusals reset with the link, and on the same condition.
       *
       * Keying this off `link` alone was wrong in the case that matters: a
       * refusal only exists when no link was established, so "plan full" for
       * the previous account survived into the next person's session and
       * suppressed their project forever. Both are statements about an account,
       * so both end when the account does.
       */
      blocked = null;
      retryAfter = 0;
      // Signing out must empty the list; signing in as someone else must not
      // leave the previous account's versions on screen.
      notifyCloud();
    }
    if (!userId) return;
    void ensureCloud();
  });

  /**
   * Write now.
   *
   * `visibilitychange` rather than `beforeunload`: on mobile a tab is very
   * often killed without ever firing an unload, and hiding is the last moment
   * we are reliably given. `pagehide` covers the desktop close.
   */
  const onHide = () => {
    if (document.visibilityState === "hidden") void controller.flush();
  };
  const onPageHide = () => {
    void controller.flush();
  };
  const onKey = (e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
      // The browser's own Save Page dialog is never what somebody pressing
      // Cmd-S in an editor wants.
      e.preventDefault();
      void controller.flush();
    }
  };

  document.addEventListener("visibilitychange", onHide);
  window.addEventListener("pagehide", onPageHide);
  document.addEventListener("keydown", onKey);

  return {
    touch() {
      void ensureCloud();
      controller.touch();
    },
    flush: () => controller.flush(),
    title: () => title,
    setTitle(next: string) {
      const cleaned = next.trim() || DEFAULT_TITLE;
      // A `change` event fires when the field is blurred whether or not anything
      // was typed, so without this every click into and out of the name box
      // writes a fresh archive.
      if (cleaned === title) return;
      title = cleaned;
      void ensureCloud();
      /*
       * Written now, not in three seconds.
       *
       * A rename is a discrete act, not a stroke, so there is nothing to
       * coalesce — and the thing people do immediately after renaming is leave,
       * which is precisely what the debounce cannot survive. Blurring the field
       * by clicking "All designs" fires `change` and then navigates, and the
       * scheduled write dies with the page. `touch` first because `flush` is a
       * no-op on a document that is not dirty.
       */
      controller.touch();
      void controller.flush();
    },

    cloudStatus() {
      if (link) return { kind: "linked", projectId: link.projectId };
      if (blocked === "plan-full") return { kind: "plan-full" };
      if (blocked === "denied") return { kind: "denied" };
      /*
       * Signed in with no link and no refusal means the create is in flight or
       * cooling off after a network failure — both are "on its way", not
       * "local". Reading `identity()` rather than `cloudUser` because
       * `cloudUser` only updates when the identity handler runs, and this is
       * called from render paths that can precede it.
       */
      return accountOf(identity()) ? { kind: "pending" } : { kind: "local" };
    },
    onCloudChange(fn) {
      cloudListeners.add(fn);
      return () => cloudListeners.delete(fn);
    },
    garment: () => garment,
    setGarment(next: GarmentType) {
      if (!DRAWABLE_GARMENTS.includes(next) || next === garment) return;
      garment = next;
      /*
       * A design nobody has named follows its garment.
       *
       * Only while it is unnamed: once somebody has typed a name, changing the
       * garment must never touch it. Renaming a person's file underneath them
       * is a worse failure than a default that says the wrong noun.
       */
      if (isUnnamed(title) && (next === "shirt" || next === "pants")) {
        title = DEFAULT_TITLES[next];
      }
      void ensureCloud();
      // Discrete, like a rename, and for the same reason: it is a decision
      // rather than a stroke, and nothing about it wants coalescing.
      controller.touch();
      void controller.flush();
    },
    restored,
    destroy() {
      stopIdentity();
      stopProjectEvents();
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("keydown", onKey);
      controller.destroy();
    },
  };
}
